import { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { ref, get } from "firebase/database";
import { rtdb } from "../services/firebase";

import { calcularFrete } from "../services/shippingService";

const CheckoutContext = createContext();

export function CheckoutProvider({ children }) {

    const [activeStep, setActiveStep] = useState(0);
    const { user, cart } = useAuth();
    const [items, setItems] = useState([]);
    const [hasSavedAddresses, setHasSavedAddresses] = useState(false);

    const [shippingOptions, setShippingOptions] = useState([]);
    const [loadingShipping, setLoadingShipping] = useState(false);

    // ===============================
    // PAGAMENTO / PAGBANK
    // ===============================
    const [orderId, setOrderId] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState("IDLE"); 
    // IDLE | CREATED | WAITING_PAYMENT | PAID | FAILED

    const [pixQrCode, setPixQrCode] = useState(null);
    const [pixCopyPaste, setPixCopyPaste] = useState(null);

    const [cardToken, setCardToken] = useState(null);
    const [cardHolder, setCardHolder] = useState(null);
    const [installments, setInstallments] = useState(1);

    function nextStep() {
        setActiveStep((prev) => prev + 1);
    }

    function prevStep() {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    }

    const [checkoutData, setCheckoutData] = useState({
        client: {
            name: "",
            email: "",
            phone: "",
            cpf: "",
        },
        address: {
            cep: "",
            street: "",
            number: "",
            complement: "",
            district: "",
            city: "",
            state: "",
            saveAsDefault: false,
        },
        shipping: {
            method: "",
            price: 0,
            prazo: "",
            carrier: "",
        },
        payment: {
            method: "",
            installments: 1,
            cardToken: null,
        },
    });

    function updateSection(section, data) {
        setCheckoutData((prev) => ({
            ...prev,
            [section]: { ...prev[section], ...data },
        }));
    }

    // Carregar produtos do carrinho do Firebase
    useEffect(() => {
        async function loadCartItems() {
            if (!cart || cart.length === 0) {
                setItems([]);
                return;
            }

            try {
                const productsData = await Promise.all(
                    cart.map(async (item) => {
                        const snap = await get(ref(rtdb, `products/${item.productId}`));
                        if (!snap.exists()) return null;

                        const product = snap.val();
                        const price = product.precos?.isPromo && product.precos?.promo
                            ? parseFloat((product.precos.promo || "0").replace(/[^\d,]/g, "").replace(",", "."))
                            : parseFloat((product.precos?.normal || "0").replace(/[^\d,]/g, "").replace(",", "."));

                        return {
                            productId: item.productId,
                            productName: product.nome,
                            flavor: item.flavor,
                            quantity: item.quantity,
                            price: isNaN(price) ? 0 : price,
                            image: product.imagens?.[0] || null,
                        };
                    })
                );

                setItems(productsData.filter(Boolean));
            } catch (err) {
                console.error("Erro ao carregar itens do carrinho:", err);
                setItems([]);
            }
        }

        loadCartItems();
    }, [cart]);

    // Preencher dados do usuário automaticamente
    useEffect(() => {
        if (user && activeStep === 0) {
            updateSection("client", {
                name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "",
                email: user.email || "",
                phone: user.phone || "",
                cpf: user.cpf || "",
            });
        }
    }, [user, activeStep]);

    // Verificar se usuário tem endereços salvos e carregar endereço padrão
    useEffect(() => {
        async function loadUserAddress() {
            if (!user?.uid || activeStep !== 1) return;

            try {
                const snap = await get(ref(rtdb, `users/client/${user.uid}`));
                if (!snap.exists()) {
                    setHasSavedAddresses(false);
                    return;
                }

                const data = snap.val();
                let address = null;
                let hasAddresses = false;

                // Priorizar endereços múltiplos (novo formato)
                if (data.addresses) {
                    const addressesList = Object.keys(data.addresses).map(key => ({
                        id: key,
                        ...data.addresses[key]
                    }));
                    hasAddresses = addressesList.length > 0;
                    address = addressesList.find(addr => addr.isDefault) || addressesList[0];
                } else if (data.address || data.adress) {
                    // Compatibilidade com formato antigo (endereço único)
                    address = data.address || data.adress;
                    hasAddresses = !!address;
                }

                setHasSavedAddresses(hasAddresses);

                if (address) {
                    updateSection("address", {
                        cep: address.zipCode || address.cep || "",
                        street: address.street || "",
                        number: address.number || "",
                        complement: address.complement || "",
                        district: address.neighborhood || address.district || "",
                        city: address.city || "",
                        state: address.state || "",
                        saveAsDefault: false, // Reset ao carregar endereço existente
                    });
                }
            } catch (err) {
                console.error("Erro ao carregar endereço do usuário:", err);
                setHasSavedAddresses(false);
            }
        }

        loadUserAddress();
    }, [user, activeStep]);

    async function fetchShippingOptions() {

      if (!checkoutData.address.cep || items.length === 0) return;

      setLoadingShipping(true);

      const result = await calcularFrete({
        cepDestino: checkoutData.address.cep,
        items,
      });

      // Normaliza retorno do Melhor Envio
      const normalized = (result || [])
        .filter((opt) => opt.price)
        .map((opt) => ({
          id: opt.id,
          method: opt.name,
          carrier: opt.company?.name || opt.company?.label,
          price: Number(opt.price),
          prazo: `${opt.delivery_time} dias`,
        }));

      setShippingOptions(normalized);
      setLoadingShipping(false);

    };

    useEffect(() => {
      if (activeStep === 2) {
        fetchShippingOptions();
      }
    }, [activeStep]);

    // ===============================
    // HELPERS
    // ===============================
    const totalAmount = (items.reduce((sum, item) => sum + item.price * item.quantity, 0) + checkoutData.shipping.price);

    const amountInCents = Math.round(totalAmount * 100);

    // ===============================
    // API — PAGBANK
    // ===============================

    async function criarOrderPagbank() {

        setPaymentStatus("CREATED");

        let paymentType = checkoutData.payment.method === "credit" ? "CREDIT_CARD" : checkoutData.payment.method === "debit" ? "DEBIT_CARD" : "PIX";

        const response = await fetch(`https://criarorderpagbank-2gw7h3kv7q-uc.a.run.app/criarOrderPagbank`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            referenceId: `ORDER_${Date.now()}`,
            customer: {
              name: checkoutData.client.name,
              email: checkoutData.client.email,
              tax_id: checkoutData.client.cpf,
            },
            items: items.map((item) => ({
              name: item.productName,
              quantity: item.quantity,
              unit_amount: Math.round(item.price * 100),
            })),
            charges: [
              {
                amount: {
                    value: amountInCents,
                    currency: "BRL"
                },
                payment_method: {
                  card: {
                    token: checkoutData.payment.cardToken,
                  },
                  type: paymentType,
                  installments: checkoutData.payment.installments,
                  capture: true,
                  soft_descriptor: "VVTECH LTDA"
                }
              }
            ],
            shipping: checkoutData.address
              ? {
                  address: {
                    street: checkoutData.address.street,
                    number: checkoutData.address.number,
                    locality: checkoutData.address.district,
                    city: checkoutData.address.city,
                    region_code: checkoutData.address.state,
                    country: "BRA",
                    postal_code: checkoutData.address.cep,
                  },
                }
              : undefined,
          }),
        });

        const data = await response.json();

        if (!response.ok) throw new Error("Erro ao criar order");

        setOrderId(data.id);
        return data.id;
    }

    async function pagarComPix(orderId) {
        setPaymentStatus("WAITING_PAYMENT");

        const response = await fetch(`https://pagarcompix-2gw7h3kv7q-uc.a.run.app/pagarComPix`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderId,
            amount: amountInCents,
          }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error("Erro PIX");

        const qr = data.qr_codes?.[0];

        setPixCopyPaste(qr?.text);
        setPixQrCode(qr?.links?.[0]?.href);

        return data;
    }

    async function pagarComCartao(orderId) {
        setPaymentStatus("PROCESSING");

        const response = await fetch(`https://pagarcomcartao-2gw7h3kv7q-uc.a.run.app/pagarComCartao`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderId,
            amount: amountInCents,
            cardToken,
            installments,
            holder: {
              name: cardHolder,
            },
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          setPaymentStatus("FAILED");
          throw new Error("Cartão recusado");
        }

        setPaymentStatus("PAID");
        return data;
    }

    // ===============================
    // FLUXO FINAL
    // ===============================
    async function finalizarCheckout() {

        if (checkoutData.payment.method !== "pix") {

            const orderId = await criarOrderPagbank();
            // await pagarComCartao(orderId);

        } else {

            await pagarComPix(orderId);

        }

    }

    return (
        <CheckoutContext.Provider
            value={{
                activeStep,
                setActiveStep,
                nextStep,
                prevStep,
                checkoutData,
                updateSection,
                items,
                hasSavedAddresses,
                user,
                shippingOptions,
                loadingShipping,
                cardToken,
                setCardToken,
                cardHolder,
                setCardHolder,
                installments,
                setInstallments,
                orderId,
                paymentStatus,
                pixQrCode,
                pixCopyPaste,
                finalizarCheckout,
            }}
        >
            {children}
        </CheckoutContext.Provider>
    );
}

export function useCheckout() {
    return useContext(CheckoutContext);
}
